import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAccess } from "../context/AccessContext";

const POWDER = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/5c97f9724bfdaad482043e8f51261955fa7fece6ffe9ee3b63347e7b3c7de85d.png";

export default function ShopSuccess() {
  const location = useLocation();
  const { clearCart } = useCart();
  const { setToken, setBidToken } = useAccess();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [details, setDetails] = useState(null);
  const cartClearedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;
    let timer;

    const poll = async () => {
      try {
        const data = await fetchCheckoutStatus(sessionId);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cartClearedRef.current) {
            clearCart();
            cartClearedRef.current = true;
          }
          if (data.tutorial_access_token) {
            setToken(data.tutorial_access_token);
          }
          if (data.brush_id_access_token) {
            setBidToken(data.brush_id_access_token);
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch {
        // Continue polling on transient errors
      }
      attempts += 1;
      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, interval);
    };

    poll();
    return () => timer && clearTimeout(timer);
  }, [location.search, clearCart, setToken, setBidToken]);

  return (
    <div
      data-testid="shop-success-page"
      className="relative min-h-[70vh]"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(249,247,243,0.92), rgba(249,247,243,0.98)), url(${POWDER})`,
        backgroundSize: "cover",
      }}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
        <div className="overline mb-4">Order Status</div>

        {status === "checking" && (
          <>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light">
              Confirming your order…
            </h1>
            <p className="mt-5 text-stone-600">Just a moment while we check with our studio.</p>
            <div className="mt-10 flex justify-center">
              <span className="inline-flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-stone-400 animate-pulse" />
                <span className="w-2 h-2 rounded-full bg-stone-400 animate-pulse [animation-delay:120ms]" />
                <span className="w-2 h-2 rounded-full bg-stone-400 animate-pulse [animation-delay:240ms]" />
              </span>
            </div>
          </>
        )}

        {status === "paid" && (
          <div data-testid="success-paid">
            <h1 className="font-serif-display text-5xl sm:text-6xl text-stone-900 font-light leading-tight">
              Thank you.
              <br />
              <em className="italic text-stone-700">Your brushes are on their way.</em>
            </h1>
            {details && (
              <p className="mt-6 text-stone-600">
                We received your payment of{" "}
                <span className="text-stone-900 font-medium">
                  ${(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}
                </span>
                . A confirmation will arrive in your inbox.
              </p>
            )}
            {details?.tutorial_access_token && (
              <div data-testid="tutorials-unlocked" className="mt-6 inline-block bg-[#F2EFEB] border border-[#E5E0D8] px-5 py-4 text-left">
                <div className="overline mb-1">Tutorials Unlocked</div>
                <p className="text-sm text-stone-700">
                  All 30 brush tutorials are now available on this device. <Link to="/catalog" className="underline">Start exploring →</Link>
                </p>
              </div>
            )}
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link
                to="/shop"
                className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors"
              >
                Keep Shopping
              </Link>
              <Link
                to="/catalog"
                className="text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] px-7 py-4 transition-colors"
              >
                Read the Guide
              </Link>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div data-testid="success-failed">
            <h1 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light">
              Something went wrong.
            </h1>
            <p className="mt-5 text-stone-600">
              Your payment was cancelled or the session expired. No charges were made.
            </p>
            <Link
              to="/cart"
              className="inline-block mt-8 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors"
            >
              Back to Cart
            </Link>
          </div>
        )}

        {status === "timeout" && (
          <div data-testid="success-timeout">
            <h1 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light">
              Still processing…
            </h1>
            <p className="mt-5 text-stone-600">
              Payment confirmation is taking longer than expected. Please check your email or contact us if charged.
            </p>
            <Link
              to="/shop"
              className="inline-block mt-8 text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] px-7 py-4 transition-colors"
            >
              Back to Shop
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
